import {
  DynamicField,
  DynamicForm,
  FieldVariant,
  MultiSelectFieldProps,
  SelectFieldProps,
  SelectOption,
  SwitchFieldProps,
  TextFieldProps,
} from '@/components/form-builder/form-builder.types';
import { PassingModeRepository } from '@/store/passing-mode-state/passing-mode-state.repository';
import { ToggleSwitchChangeEvent } from 'primeng/toggleswitch';
import { passingModeStep, passingModeType } from '@/types';
import { passingModeStepLabels, passingModeTypeLabels } from '@/pages/settings/passing-mode/utils/type-labels';

interface IPassingModeFormObjectProps {
  store: PassingModeRepository;
  typeMarchersOptions?: SelectOption[];
}

export const getPassingModeFormObject = ({ store, typeMarchersOptions }: IPassingModeFormObjectProps): DynamicForm => {
  const libelleField: DynamicField<TextFieldProps> = {
    label: 'settings.passingMode.form.libelle.label',
    variant: FieldVariant.TEXT,
    isRequired: true,
    error: store.getNestedObservable<string>('errors', 'libelle'),
    props: {
      value: store.getNestedObservable<string>('dto', 'libelle'),
      onChange: (value: string) => {
        store.setNested('dto', 'libelle', value);
        store.setNested('errors', 'libelle', '');
      },
    },
  };

  const isRestraintField: DynamicField<SwitchFieldProps> = {
    label: '',
    variant: FieldVariant.SWITCH,
    error: store.getNestedObservable<string>('errors', 'restraint'),
    props: {
      placeholder: 'settings.passingMode.form.isRestraint.label',
      checked: store.getNestedObservable<boolean>('dto', 'restraint'),
      onChange: (event: ToggleSwitchChangeEvent) => {
        const checked = event && (event.checked ?? event);
        store.setNested('dto', 'restraint', checked, false);
        store.setNested('errors', 'restraint', '');
      },
    },
  };

  const typeMarchersField: DynamicField<MultiSelectFieldProps> = {
    label: 'settings.passingMode.form.typeMarchers.label',
    variant: FieldVariant.MULTISELECT,
    isRequired: true,
    error: store.getNestedObservable<string>('errors', 'typeMarchersIds'),
    props: {
      placeholder: 'settings.passingMode.form.typeMarchers.placeholder',
      options: typeMarchersOptions?.map(market => {
        return { name: market.name, code: market.code };
      }),
      value: store.getObservable('typeMarchersSelected'),
      onSelectChange: (value: SelectOption[]) => {
        store.setNested('dto', 'typeMarchersIds', value && value.length > 0 ? value.map(market => market.code) : []);
        store.set('typeMarchersSelected', value);
      },
    },
  };

  const passingModeTypeField: DynamicField<SelectFieldProps> = {
    label: 'settings.passingMode.form.type.label',
    variant: FieldVariant.SELECT,
    isRequired: true,
    error: store.getNestedObservable<string>('errors', 'type'),
    props: {
      placeholder: 'settings.passingMode.form.type.placeholder',
      options: Object.values(passingModeType).map(value => {
        return { name: passingModeTypeLabels[value], code: value } as SelectOption;
      }),
      value: store.getObservable('passingModeTypeSelected'),
      onSelectChange: (value: SelectOption) => {
        store.setNested('dto', 'type', value.code);
        store.set('passingModeTypeSelected', value);
      },
    },
  };

  const passingModeStepField: DynamicField<SelectFieldProps> = {
    label: 'settings.passingMode.form.step.label',
    variant: FieldVariant.SELECT,
    isRequired: true,
    error: store.getNestedObservable<string>('errors', 'etapes'),
    props: {
      placeholder: 'settings.passingMode.form.step.placeholder',
      options: Object.values(passingModeStep).map(value => {
        return { name: passingModeStepLabels[value], code: value } as SelectOption;
      }),
      value: store.getObservable('passingModeStepSelected'),
      onSelectChange: (value: SelectOption) => {
        store.setNested('dto', 'etapes', value.code);
        store.set('passingModeStepSelected', value);
      },
    },
  };
  return {
    title: 'settings.passingMode.form.title',
    description: '',
    isHeaderHidden: true,
    grids: [
      {
        title: '',
        isHeaderHidden: true,
        gridItems: [
          {
            fields: [libelleField],
          },
          {
            fields: [isRestraintField],
          },
          {
            fields: [typeMarchersField],
          },
          {
            fields: [passingModeTypeField],
          },
          {
            fields: [passingModeStepField],
          },
        ],
      },
    ],
  };
};
